// The editor of a library entry, for New Block, Edit, and Duplicate on the Block Library tab: a
// Title field above the content fields of the Add Block dialog. The tab mounts it for one use,
// and it calls `onClosed` after its close animation.
import { useRef, useState } from "react";
import { Form, Input, Modal } from "antd";
import type { InputRef } from "antd";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { BLOCK_LIBRARY_TITLE_MAX_LENGTH, getRtpErrorMessage } from "@utils/rtp_utils";
import { rtpApi } from "./rtpHooks";
import type { RtpBlockLibraryState } from "./rtpHooks";
import { BlockContentFields, blockContentToFormValues, buildBlockContent, hasBlockContent } from "./BlockForm";
import type { BlockFormValues } from "./BlockForm";
import type { RtpBlockTemplate } from "./types";

interface EntryFormValues extends BlockFormValues {
	title: string;
}

interface BlockLibraryEntryEditorProps {
	// The entry to edit, or null for a new entry.
	entry: RtpBlockTemplate | null;
	library: RtpBlockLibraryState;
	// Enables the player-stats hint under the target rows.
	player?: { id: number; name: string };
	onClosed: () => void;
}

export const BlockLibraryEntryEditor = ({ entry, library, player, onClosed }: BlockLibraryEntryEditorProps) => {
	const [form] = Form.useForm<EntryFormValues>();
	const titleRef = useRef<InputRef>(null);
	const [open, setOpen] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [contentError, setContentError] = useState(false);

	const submit = async () => {
		if (submitting) return;
		// Checked before validateFields, so a missing title and missing content both show at once.
		const noContent = !hasBlockContent(buildBlockContent(form.getFieldsValue()));
		setContentError(noContent);
		let values: EntryFormValues;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}
		if (noContent) return;
		setSubmitting(true);
		const body = { title: values.title.trim(), content: buildBlockContent(values) };
		try {
			if (entry) await rtpApi.updateRtpBlockTemplate(entry.id, body);
			else await rtpApi.createRtpBlockTemplate(body);
			await library.reload();
			setOpen(false);
			message.success(entry ? "Library block saved." : "Library block added.");
		} catch (e) {
			// A 400 is a rule of the library (for example a used title): the user can fix it here.
			const reason = getRtpErrorMessage(e);
			if ((e as { response?: { status?: number } })?.response?.status === 400) {
				form.setFields([{ name: "title", errors: [reason] }]);
			} else {
				message.error(reason);
			}
		}
		setSubmitting(false);
	};

	return (
		<Modal
			className="rtp-wz rtp-crit-modal"
			title={entry ? "Edit library block" : "New library block"}
			open={open}
			width={560}
			okText="Save"
			confirmLoading={submitting}
			onOk={submit}
			onCancel={() => setOpen(false)}
			// The title gets the focus once, when the dialog is open. A ref function runs on each
			// render, so it would select the text on each key.
			afterOpenChange={(visible) => {
				if (visible) titleRef.current?.focus({ preventScroll: true, cursor: "all" });
			}}
			afterClose={onClosed}
			okButtonProps={{ className: "rtp-wz-primary-btn", "data-attr": "settings.rtp-block-library.editor-save" } as any}
			cancelButtonProps={{ "data-attr": "settings.rtp-block-library.editor-cancel" } as any}
		>
			<Form
				form={form}
				layout="vertical"
				initialValues={entry ? { title: entry.title, ...blockContentToFormValues(entry.content) } : undefined}
			>
				<Form.Item name="title" label="Title" rules={[{ required: true, whitespace: true, message: "Enter a title." }]}>
					<Input
						ref={titleRef}
						maxLength={BLOCK_LIBRARY_TITLE_MAX_LENGTH}
						placeholder="Title"
						onPressEnter={submit}
					/>
				</Form.Item>
				<BlockContentFields player={player} contentError={contentError} />
			</Form>
		</Modal>
	);
};
